import React, {useState, useEffect} from 'react'
import styled, {createGlobalStyle} from 'styled-components'
import {ThemeProvider, themeGet} from '../..'
import type {Theme} from '../../components/ThemeProvider/ThemeProvider'
import {appfire, atlassian, formula, monday} from '../../experimental/Theme'
import defaultTheme from '../../theme'

const themes: Record<string, Theme> = {
  defaultTheme,
  appfire,
  atlassian,
  monday,
  formula
}

// set global theme styles and do some quality of life css resets
const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
  }
  body, html, #root {
    height: 100%
  }
  body {
    -webkit-font-smoothing: antialiased;
    background-color: ${themeGet('colors.elevation.surface.default.[default]')};
    font-family: ${themeGet('fontFamily.sansSerif')};
    color: ${themeGet('colors.text.default')};
  }
  input, button, textarea, select {
    font: inherit;
  }
`
// only remove padding for multi-theme view grid
const GlobalStyleMultiTheme = createGlobalStyle`
  body {
    padding: 0 !important;
  }
`

// duo theme view, this can be extended for more themes
const Wrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 100vh;
  /* height: 100%; */
`

// instead of global theme, only theme wrapper for each story
const ThemedSectionStyle = styled.div`
  background-color: ${themeGet('colors.elevation.surface.default.[default]')};
  color: ${themeGet('colors.text.default')};
  padding: 1rem;
`

export const toolbarTypes = {
  colorMode: {
    name: 'Color mode',
    description: 'Color mode (day, night, auto, all)',
    defaultValue: 'day',
    toolbar: {
      icon: 'paintbrush',
      // array of colorMode items
      items: ['day', 'night', 'auto', 'all'],
      showName: true
    }
  },
  theme: {
    name: 'Ecosystem Theme',
    description: 'Select theme',
    defaultValue: 'defaultTheme',
    toolbar: {
      icon: 'mirror',
      items: ['defaultTheme', 'brand', 'monday', 'atlassian', 'formula', 'appfire'],
      showName: true
    }
  }
  // @TODO: revisit if we ever need to implement accessibility modes, color blind modes, dark-dimmed, light high contrast, etc
  // dayScheme: {
  //   name: 'Day color scheme',
  //   description: 'Day color scheme',
  //   defaultValue: 'light',
  //   toolbar: {
  //     icon: 'circlehollow',
  //     items: Object.keys(theme.colorSchemes),
  //     showName: true
  //   }
  // },
  // nightScheme: {
  //   name: 'Night color scheme',
  //   description: 'Night color scheme',
  //   defaultValue: 'dark',
  //   toolbar: {
  //     icon: 'circle',
  //     items: Object.keys(theme.colorSchemes),
  //     showName: true
  //   }
  // }
}

// context.globals.X references items in globalTypes
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const withThemeProvider = (Story: any, context: any) => {
  // const themeName = context.globals.theme || 'defaultTheme'
  // const selectedTheme = themeName === 'defaultTheme' ? themes.defaultTheme : themes[themeName]
  const [selectedTheme, setSelectedTheme] = useState(themes.defaultTheme)
  useEffect(() => {
    const themeName = context.globals.theme || 'defaultTheme'
    const theme = themeName === 'defaultTheme' ? themes.defaultTheme : themes[themeName]
    setSelectedTheme(theme)
  }, [context.globals.theme])

  const themeKey = `${context.id}`

  if (context.globals.colorMode === 'all') {
    return (
      <Wrapper>
        <GlobalStyleMultiTheme />
        <ThemeProvider
          key={themeKey}
          theme={selectedTheme}
          colorMode="day"
          dayScheme={context.globals.dayScheme}
          nightScheme={context.globals.nightScheme}
        >
          <ThemedSectionStyle>
            <Story {...context} />
          </ThemedSectionStyle>
        </ThemeProvider>
        <ThemeProvider
          key={themeKey}
          theme={selectedTheme}
          colorMode="night"
          dayScheme={context.globals.dayScheme}
          nightScheme={context.globals.nightScheme}
        >
          <ThemedSectionStyle>
            <Story {...context} />
          </ThemedSectionStyle>
        </ThemeProvider>
      </Wrapper>
    )
  }

  return (
    <ThemeProvider
      key={themeKey}
      theme={selectedTheme}
      colorMode={context.globals.colorMode}
      dayScheme={context.globals.dayScheme}
      nightScheme={context.globals.nightScheme}
    >
      <GlobalStyle />
      <Story {...context} />
    </ThemeProvider>
  )
}
